import React, { useState, useEffect } from "react";
import { Card, Input } from "antd";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import api from "../interceptors/api";
import StandardButton from "../components/StandardButton";

const SMTPTester = () => {
  const [form, setForm] = useState({
    host: "",
    port: "587",
    secure: false,
    user: "",
    pass: "",
    to: "",
    subject: t("default_email_subject"),
    text: t("default_email_body"),
  });
  const [output, setOutput] = useState("");
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  useEffect(() => {
    const id = searchParams.get("share");
    if (id) {
      api.get(`/smtp-tester/share/${id}`)
        .then(res => {
          const data = res.data;
          if (data && data.form) {
            setForm(data.form);
          }
        })
        .catch((err) => {
          toast.error(t('server_error') + ": " + err.message);
        });
    }
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({
      ...form,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleShare = async () => {
    try {
      const res = await api.post(`/smtp-tester/share`, { form });
      const data = res.data;
      if (data.id) {
        const url = `${window.location.origin}/tools/smtp-tester?share=${data.id}`;
        await navigator.clipboard.writeText(url);
        toast.success(t('share_success'));
      }
    } catch {
      toast.error(t('share_failed'));
    }
  };

  const handleSend = async () => {
    const toastId = toast.loading(t("sending_request"));
    try {
      const response = await api.post(`/smtp-tester`, form);

      const data = response.data;
      if (data.result) {
        setOutput(data.result);
        toast.success(t('email_sent_success'));
      } else {
        setOutput(t("error") + ": " + data.error);
        toast.error(t('server_error') + ": " + data.error);
      }
    } catch (err) {
      setOutput(t('server_error'));
      toast.error(t('server_connection_failed'));
    } finally {
      toast.dismiss(toastId);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto">
        <Card className="shadow-lg rounded-xl p-4 space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-indigo-700 mb-2">{t('smtp_information')}</h2>
            <div className="flex flex-col gap-3">
              <Input name="host" placeholder={t('smtp_host_placeholder')} value={form.host} onChange={handleChange} />
              <Input name="port" placeholder={t('smtp_port_placeholder')} value={form.port} onChange={handleChange} />
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" name="secure" checked={form.secure} onChange={handleChange} />
                {t('use_secure_ssl')}
              </label>
              <Input name="user" placeholder={t('smtp_username_placeholder')} value={form.user} onChange={handleChange} />
              <Input name="pass" type="password" placeholder={t('smtp_password_placeholder')} value={form.pass} onChange={handleChange} />
            </div>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-indigo-700 mb-2">{t('sample_email_details')}</h2>
            <div className="flex flex-col gap-3">
              <Input name="to" placeholder={t('to_email_placeholder')} value={form.to} onChange={handleChange} />
              <Input name="subject" placeholder={t('subject_placeholder')} value={form.subject} onChange={handleChange} />
              <Input.TextArea rows={4} name="text" placeholder={t('message_placeholder')} value={form.text} onChange={handleChange} />
            </div>
          </div>
          <div className="mt-4 mb-4">
            <StandardButton
              onClick={handleSend}
              type="secondary"
              className="w-full"
            >
              {t('send_test_email_button')}
            </StandardButton>
          </div>

          {output && (
            <div className="bg-indigo-50 p-4 rounded-md border border-indigo-200 whitespace-pre-wrap">
              <strong className="text-indigo-700">{t('result_label')}</strong>
              <p className="break-all mt-2 text-gray-800">{output}</p>
              <div className="mt-4 flex gap-3">
                <StandardButton
                  onClick={handleShare}
                  type="secondary"
                  variant="outlined"
                  size="small"
                >
                  {t('share_button')}
                </StandardButton>
              </div>
            </div>
          )}
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default SMTPTester;